import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSnack } from "../../../hooks/useSnack";
import teacherServices from "../../../services/teacherServices";
import { formatCustomDate } from "../../../utils/formatCustomDate";
import Button from "../../Button";
import CustomDataTable from "../../CustomDataTable";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";

const MyProjectsModal = forwardRef(({ onSubmit, onSubmitCreate }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const { showSnack } = useSnack();

  useImperativeHandle(ref, () => ({
    openModal(projectData) {
      setProjectData(projectData);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await teacherServices.getTeacherProjects(projectData?.IDEType);
      setProjects(response?.data);
    } catch (error) {
      showSnack(error, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleRowClick = async (id) => {
    const response = await teacherServices.getTeacherProjectByID(id);
    onSubmit(response);
    setIsOpen(false);
  };

  const handleCreateProject = () => {
    onSubmitCreate();
    setIsOpen(false);
  };

  const columns = [
    {
      name: t("project_name"),
      field: "name",
      sortable: true,
      body: (rowData) => (
        <Button
          tooltip={rowData.name}
          labelStyle="truncate block w-36 overflow-hidden whitespace-nowrap text-start"
          label={rowData.name}
          variant="link"
          onClick={() => handleRowClick(rowData.id)}
        />
      ),
    },
    {
      name: t("creation_date"),
      field: "creation_date",
      sortable: true,
      body: (rowData) => <span className="font-medium">{formatCustomDate(rowData.created_at)}</span>,
    },
    {
      name: t("modification_date"),
      field: "modification_date",
      sortable: true,
      body: (rowData) => <span className={`font-medium`}>{formatCustomDate(rowData.last_updated_at)}</span>,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      className="w-full max-w-2xl px-0"
      contentClassname="p-0"
      header={
        <p className="capitalize text-blue-500">
          {t("my_projects")} - {projectData?.IDEType}
        </p>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button color="white" variant="outlined" onClick={handleCloseModal} label={t("close")} />
          <Button onClick={handleCreateProject} label={t("create_new_project")} />
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <CustomDataTable header={false} columns={columns} data={projects} withPagination withSort />
      )}
    </Modal>
  );
});

export default MyProjectsModal;
